// src/modules/panel-cupones.js — Panel de gestión de cupones (secreto, solo dueño).
//
// FASE cupones · Parte B (1-jun-2026). Cablea las RPCs admin de la Parte A:
//   admin_set_pin, admin_crear_cupon, admin_listar_cupones, admin_editar_vencimiento.
//
// PRINCIPIO DE SEGURIDAD: la ruta (PANEL_ROUTE) es solo ofuscación — NO está
// linkeada desde ningún menú. La seguridad REAL son las RPCs SECURITY DEFINER
// con guard dueño + PIN (Parte A). Encontrar la URL no habilita nada: un random
// sin sesión ve un 404 cosmético; un logueado-no-dueño ve la caja de PIN pero
// cada RPC le responde `no_autorizado`.
//
// EL PIN VIVE SOLO EN MEMORIA (variable de módulo `pinMem`) durante la sesión.
// NUNCA se escribe en localStorage/sessionStorage. Se manda en cada llamada y
// se pierde al recargar o navegar fuera.

import { appRoot } from '../utils/dom.js'
import { supabase } from '../supabase.js'
import { getCurrentUser } from '../core/auth.js'
import { normalizarCodigoCupon } from '../utils/cupon.js'

// Ruta oculta fija, no adivinable, fuera de todo menú/link.
export const PANEL_ROUTE = '/panel-pm-7gk92x'

// PIN solo en memoria — jamás persistido.
let pinMem = ''

// ─── Errores crudos de las RPCs → mensajes amigables ────────────────────────
const friendlyError = (raw) => {
  const m = (raw || '').toLowerCase()
  if (m.includes('pin_invalido'))         return 'PIN incorrecto.'
  if (m.includes('pin_no_configurado'))   return 'Falta configurar el PIN inicial (usá "Cambiar PIN").'
  if (m.includes('pin_actual_invalido'))  return 'El PIN actual no coincide.'
  if (m.includes('pin_nuevo_vacio'))      return 'El PIN nuevo no puede estar vacío.'
  if (m.includes('no_autorizado'))        return 'No autorizado: esta cuenta no es la del dueño.'
  if (m.includes('codigo_duplicado'))     return 'Ese código de cupón ya existe.'
  if (m.includes('codigo_vacio'))         return 'Escribí el código del cupón.'
  if (m.includes('porcentaje_invalido'))  return 'El % debe ir de a 10 (10, 20… 100).'
  if (m.includes('vencimiento_invalido')) return 'El vencimiento tiene que ser una fecha futura.'
  if (m.includes('usos_invalido'))        return 'La cantidad de usos no es válida.'
  if (m.includes('cupon_inexistente'))    return 'No existe ese cupón.'
  return raw || 'Algo salió mal. Probá de nuevo.'
}

const callRpc = async (name, args) => {
  const { data, error } = await supabase.rpc(name, args)
  if (error) return { ok: false, err: friendlyError(error.message) }
  return { ok: true, data }
}

// vencimiento del <input type="date"> ('YYYY-MM-DD') → ISO fin-del-día local.
const fechaToISO = (val) => {
  if (!val) return null
  const d = new Date(`${val}T23:59:59`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}
const fmtFecha = (iso) => {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) }
  catch { return iso }
}
const beneficio = (pct) => (pct === 100 ? 'Regalo (100%)' : `${pct}% off`)
const usoXdeY = (act, max) => `${act ?? 0} de ${max == null ? '∞' : max}`

// ─── CSS local (prefijo .pnl-*) ─────────────────────────────────────────────
const CSS = `
  .pnl-wrap { width:100%; max-width:560px; margin:0 auto; padding:1rem 0; }
  .pnl-header { background:linear-gradient(135deg,#dc2626,#991b1b); color:#fff; padding:0.8rem 1rem; border-radius:8px; text-align:center; margin-bottom:1.2rem; font-weight:900; letter-spacing:0.5px; text-transform:uppercase; font-size:0.85rem; }
  .pnl-section { border:1px solid rgba(255,255,255,0.14); background:rgba(255,255,255,0.03); border-radius:12px; padding:1rem 1rem 1.1rem; margin-bottom:1.1rem; }
  .pnl-section h3 { color:var(--white); font-family:var(--font-heading); font-size:1rem; margin:0 0 0.7rem; text-transform:uppercase; letter-spacing:0.4px; }
  .pnl-field { display:flex; flex-direction:column; gap:0.25rem; margin-bottom:0.7rem; }
  .pnl-field label { color:var(--text-muted); font-size:0.72rem; font-weight:700; letter-spacing:0.3px; text-transform:uppercase; }
  .pnl-field input, .pnl-field select { padding:0.55rem 0.6rem; border:1px solid rgba(255,255,255,0.2); border-radius:8px; background:rgba(255,255,255,0.05); color:var(--white); font-size:0.95rem; }
  .pnl-field input:focus, .pnl-field select:focus { outline:2px solid var(--celeste); border-color:var(--celeste); }
  .pnl-row { display:flex; gap:0.6rem; align-items:flex-end; }
  .pnl-row .pnl-field { flex:1; margin-bottom:0; }
  .pnl-inline-check { display:flex; align-items:center; gap:0.4rem; color:var(--text-muted); font-size:0.8rem; font-weight:700; white-space:nowrap; padding-bottom:0.55rem; }
  .pnl-inline-check input { width:auto; }
  .pnl-btn { width:100%; margin-top:0.5rem; padding:0.7rem; font-size:0.9rem; font-weight:800; background:linear-gradient(135deg,var(--celeste),#a855f7); color:#fff; border:none; border-radius:8px; cursor:pointer; letter-spacing:0.3px; }
  .pnl-btn:disabled { background:rgba(255,255,255,0.08); color:rgba(255,255,255,0.4); cursor:not-allowed; }
  .pnl-btn.secundario { background:transparent; border:1px solid rgba(255,255,255,0.2); color:var(--white); }
  .pnl-msg { display:none; margin-top:0.6rem; padding:0.5rem 0.6rem; border-radius:6px; font-size:0.8rem; text-align:center; font-weight:700; }
  .pnl-msg.error { display:block; background:rgba(255,107,107,0.1); border:1px solid rgba(255,107,107,0.3); color:#ff6b6b; }
  .pnl-msg.ok { display:block; background:rgba(61,220,132,0.08); border:1px solid rgba(61,220,132,0.28); color:#bbf7d0; }
  .pnl-pin-note { color:var(--text-muted); font-size:0.7rem; margin-top:0.35rem; line-height:1.3; }
  .pnl-list { display:flex; flex-direction:column; gap:0.55rem; }
  .pnl-card { border:1px solid rgba(255,255,255,0.12); border-radius:10px; background:rgba(255,255,255,0.025); padding:0.65rem 0.75rem; }
  .pnl-card-top { display:flex; justify-content:space-between; align-items:center; gap:0.5rem; }
  .pnl-card-cod { font-family:var(--font-heading); color:var(--white); font-size:1rem; font-weight:900; letter-spacing:0.5px; overflow-wrap:anywhere; word-break:break-word; min-width:0; }
  .pnl-card-ben { color:var(--celeste); font-size:0.8rem; font-weight:800; }
  .pnl-card-meta { display:flex; flex-wrap:wrap; gap:0.5rem 1rem; margin-top:0.4rem; color:var(--text-muted); font-size:0.74rem; }
  .pnl-card-meta strong { color:rgba(248,250,252,0.9); font-weight:800; }
  .pnl-card-edit { margin-top:0.5rem; padding:0.35rem 0.7rem; font-size:0.72rem; font-weight:800; background:rgba(117,170,219,0.08); border:1px solid rgba(117,170,219,0.25); color:var(--celeste); border-radius:999px; cursor:pointer; }
  .pnl-empty { color:var(--text-muted); font-size:0.82rem; text-align:center; padding:0.8rem 0; }
  .pnl-404 { max-width:420px; margin:4rem auto; text-align:center; color:var(--text-muted); }
  .pnl-404 h2 { color:var(--white); font-family:var(--font-heading); font-size:1.4rem; margin-bottom:0.5rem; }
  .pnl-404 a { color:var(--celeste); font-weight:700; }
`

// ─── 404 cosmético (sin pista de que existe un panel) ───────────────────────
const render404 = () => {
  appRoot.innerHTML = `
    <style>${CSS}</style>
    <div class="pnl-404">
      <h2>Página no encontrada</h2>
      <p>El enlace no existe o no está disponible.</p>
      <p><a href="#/">Volver al inicio</a></p>
    </div>`
}

const PCT_OPTIONS = [10,20,30,40,50,60,70,80,90,100]

// ─── Render principal ───────────────────────────────────────────────────────
export const renderPanelCupones = () => {
  if (!appRoot) return
  // Gate cosmético: sin sesión → 404. La verdad de "sos el dueño" la imponen las RPCs.
  if (!getCurrentUser()) { render404(); return }

  appRoot.innerHTML = `
    <style>${CSS}</style>
    <div class="pnl-wrap">
      <div class="pnl-header">Gestión de cupones · panel privado</div>

      <div class="pnl-section">
        <h3>PIN</h3>
        <div class="pnl-field">
          <label for="pnl-pin">PIN del dueño</label>
          <input id="pnl-pin" type="password" autocomplete="off" placeholder="Tu PIN" value="${pinMem ? '' : ''}">
          <span class="pnl-pin-note">Se usa solo en memoria y se manda en cada acción. Nunca se guarda en el dispositivo.</span>
        </div>
      </div>

      <div class="pnl-section">
        <h3>Crear cupón</h3>
        <div class="pnl-field">
          <label for="pnl-cod">Código (sin límite)</label>
          <input id="pnl-cod" type="text" placeholder="Ej: MUNDIAL2026">
        </div>
        <div class="pnl-row">
          <div class="pnl-field">
            <label for="pnl-pct">Beneficio</label>
            <select id="pnl-pct">
              ${PCT_OPTIONS.map(p => `<option value="${p}"${p===100?' selected':''}>${p===100?'Regalo (100%)':p+'% off'}</option>`).join('')}
            </select>
          </div>
          <div class="pnl-field">
            <label for="pnl-usos">Usos</label>
            <input id="pnl-usos" type="number" min="1" inputmode="numeric" placeholder="Ej: 50">
          </div>
          <span class="pnl-inline-check"><input id="pnl-inf" type="checkbox"> Infinito</span>
        </div>
        <div class="pnl-row">
          <div class="pnl-field">
            <label for="pnl-venc">Vencimiento (obligatorio)</label>
            <input id="pnl-venc" type="date">
          </div>
          <div class="pnl-field">
            <label for="pnl-org">Origen / campaña</label>
            <input id="pnl-org" type="text" placeholder="Ej: instagram-junio">
          </div>
        </div>
        <button class="pnl-btn" id="pnl-crear">Crear cupón</button>
        <div class="pnl-msg" id="pnl-crear-msg"></div>
      </div>

      <div class="pnl-section">
        <h3>Cupones</h3>
        <button class="pnl-btn secundario" id="pnl-listar">Actualizar lista</button>
        <div class="pnl-msg" id="pnl-list-msg"></div>
        <div class="pnl-list" id="pnl-list"></div>
      </div>

      <div class="pnl-section">
        <h3>Cambiar PIN</h3>
        <div class="pnl-field">
          <label for="pnl-pin-act">PIN actual (vacío si es la primera vez)</label>
          <input id="pnl-pin-act" type="password" autocomplete="off" placeholder="PIN actual">
        </div>
        <div class="pnl-field">
          <label for="pnl-pin-nue">PIN nuevo</label>
          <input id="pnl-pin-nue" type="password" autocomplete="off" placeholder="PIN nuevo">
        </div>
        <button class="pnl-btn secundario" id="pnl-set-pin">Guardar PIN</button>
        <div class="pnl-msg" id="pnl-pin-msg"></div>
      </div>
    </div>`

  wire()
}

// ─── Wiring ─────────────────────────────────────────────────────────────────
const setMsg = (el, text, kind) => {
  if (!el) return
  el.textContent = text
  el.className = `pnl-msg ${kind}`
}

const wire = () => {
  const $ = (id) => document.getElementById(id)
  const pinInput = $('pnl-pin')
  pinInput?.addEventListener('input', () => { pinMem = pinInput.value })
  if (pinInput && pinMem) pinInput.value = pinMem  // restaura si volvimos a la pantalla

  // Infinito ⇄ usos
  const infChk = $('pnl-inf')
  const usosInp = $('pnl-usos')
  infChk?.addEventListener('change', () => {
    usosInp.disabled = infChk.checked
    if (infChk.checked) usosInp.value = ''
  })

  // Crear cupón
  $('pnl-crear')?.addEventListener('click', async () => {
    const msg = $('pnl-crear-msg')
    if (!pinMem) { setMsg(msg, 'Ingresá el PIN arriba.', 'error'); return }
    const codigo = normalizarCodigoCupon($('pnl-cod').value)
    const porcentaje = Number($('pnl-pct').value)
    const infinito = infChk.checked
    const usos = infinito ? null : Number(usosInp.value)
    const vencISO = fechaToISO($('pnl-venc').value)
    const origen = $('pnl-org').value.trim() || null

    if (!codigo) { setMsg(msg, 'Escribí el código del cupón.', 'error'); return }
    if (!vencISO) { setMsg(msg, 'Elegí la fecha de vencimiento.', 'error'); return }
    if (!infinito && (!Number.isInteger(usos) || usos < 1)) { setMsg(msg, 'Poné un número de usos (o tildá Infinito).', 'error'); return }

    setMsg(msg, 'Creando…', 'ok')
    const r = await callRpc('admin_crear_cupon', {
      p_pin: pinMem, p_codigo: codigo, p_porcentaje: porcentaje,
      p_usos_maximos: usos, p_vencimiento: vencISO, p_origen: origen,
    })
    if (!r.ok) { setMsg(msg, r.err, 'error'); return }
    setMsg(msg, `Cupón ${codigo} creado.`, 'ok')
    $('pnl-cod').value = ''; usosInp.value = ''; $('pnl-org').value = ''; $('pnl-venc').value = ''
    infChk.checked = false; usosInp.disabled = false
    listar()  // refresca
  })

  // Listar
  $('pnl-listar')?.addEventListener('click', listar)

  // Cambiar / definir PIN
  $('pnl-set-pin')?.addEventListener('click', async () => {
    const msg = $('pnl-pin-msg')
    const actual = $('pnl-pin-act').value
    const nuevo = $('pnl-pin-nue').value
    if (!nuevo) { setMsg(msg, 'Escribí el PIN nuevo.', 'error'); return }
    setMsg(msg, 'Guardando…', 'ok')
    const r = await callRpc('admin_set_pin', { p_pin_actual: actual || null, p_pin_nuevo: nuevo })
    if (!r.ok) { setMsg(msg, r.err, 'error'); return }
    setMsg(msg, 'PIN actualizado. Usalo arriba para operar.', 'ok')
    $('pnl-pin-act').value = ''; $('pnl-pin-nue').value = ''
  })
}

// ─── Listado de cupones ─────────────────────────────────────────────────────
const listar = async () => {
  const listEl = document.getElementById('pnl-list')
  const msg = document.getElementById('pnl-list-msg')
  if (!pinMem) { setMsg(msg, 'Ingresá el PIN arriba para ver la lista.', 'error'); return }
  setMsg(msg, 'Cargando…', 'ok')
  const r = await callRpc('admin_listar_cupones', { p_pin: pinMem })
  if (!r.ok) { setMsg(msg, r.err, 'error'); if (listEl) listEl.innerHTML = ''; return }
  const cupones = Array.isArray(r.data) ? r.data : []
  setMsg(msg, '', '')
  msg.style.display = 'none'
  if (!cupones.length) { listEl.innerHTML = `<div class="pnl-empty">Todavía no hay cupones.</div>`; return }

  listEl.innerHTML = cupones.map((c) => `
    <div class="pnl-card">
      <div class="pnl-card-top">
        <span class="pnl-card-cod">${c.codigo}</span>
        <span class="pnl-card-ben">${beneficio(c.porcentaje)}</span>
      </div>
      <div class="pnl-card-meta">
        <span>Usados: <strong>${usoXdeY(c.usos_actuales, c.usos_maximos)}</strong></span>
        <span>Vence: <strong>${fmtFecha(c.vencimiento)}</strong></span>
        ${c.origen ? `<span>Origen: <strong>${c.origen}</strong></span>` : ''}
      </div>
      <button class="pnl-card-edit" data-edit="${c.codigo}">Editar vencimiento</button>
    </div>`).join('')

  listEl.querySelectorAll('[data-edit]').forEach((btn) => {
    btn.addEventListener('click', () => editarVencimiento(btn.getAttribute('data-edit')))
  })
}

const editarVencimiento = async (codigo) => {
  const msg = document.getElementById('pnl-list-msg')
  if (!pinMem) { setMsg(msg, 'Ingresá el PIN arriba.', 'error'); return }
  const val = window.prompt(`Nuevo vencimiento para ${codigo} (formato AAAA-MM-DD):`)
  if (val == null) return
  const iso = fechaToISO(val.trim())
  if (!iso) { setMsg(msg, 'Fecha inválida. Usá AAAA-MM-DD.', 'error'); return }
  setMsg(msg, 'Actualizando…', 'ok')
  const r = await callRpc('admin_editar_vencimiento', { p_pin: pinMem, p_codigo: codigo, p_nuevo_vencimiento: iso })
  if (!r.ok) { setMsg(msg, r.err, 'error'); return }
  setMsg(msg, `Vencimiento de ${codigo} actualizado.`, 'ok')
  listar()
}
